/**
 * BE-1293: удержание фокуса внутри оверлея — чистыми функциями, без DI и директив.
 *
 * Вынесено из компонента модального окна (BE-1239), чтобы выезжающая панель не копировала
 * девяносто строк тонкого поведения вместе с неочевидными решениями внутри: копия
 * разъехалась бы с оригиналом на первой же правке. Оба потребителя — `shared/ui-modal`
 * и `shared/ui-sheet`.
 *
 * Функции работают с ЛЮБЫМ контейнером, поэтому подложку оверлея в обход не втянут, если
 * передавать сам диалог, а не корень: у панели подложка сделана кнопкой и лежит рядом с
 * контейнером, а не внутри него.
 */

// disabled отсекается фильтром в focusableItems — в том числе у элементов с tabindex,
// до которых `:not([disabled])` в селекторе всё равно бы не дотянулся.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Фокусируемые элементы контейнера в порядке разметки.
 *
 * Считается на КАЖДОЕ нажатие, а не один раз при открытии: содержимое оверлея меняется
 * по ходу жизни — ветки загрузки/ошибки/формы, а в окне прекращения акции текст
 * соглашения приходит с бэка через innerHTML и несёт собственный tabindex.
 */
export function focusableItems(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter(
    // getClientRects, а не offsetParent: поповеры внутри оверлея позиционируются fixed,
    // и offsetParent у них null при том, что они видимы и фокусируемы.
    (el) => !el.hasAttribute('disabled') && el.getClientRects().length > 0,
  );
}

/**
 * Замыкает обход по Tab внутри контейнера. Событие перехватывается только на краях
 * обхода и когда фокус оказался снаружи — в середине его обрабатывает браузер сам.
 */
export function wrapTabFocus(
  container: HTMLElement,
  event: Event,
  backwards = (event as KeyboardEvent).shiftKey,
): void {
  const items = focusableItems(container);
  if (items.length === 0) {
    // Оверлей без единого фокусируемого элемента: держим фокус на контейнере.
    event.preventDefault();
    container.focus();
    return;
  }

  const first = items[0];
  const last = items[items.length - 1];
  // Куда заводим фокус и с какого края заворачиваем — зеркально для Shift+Tab.
  const entry = backwards ? last : first;
  const edge = backwards ? first : last;

  const active = document.activeElement;
  // Фокус на контейнере или под подложкой — тоже заводим внутрь, а не оставляем снаружи.
  const outside =
    !active || active === container || !container.contains(active);
  if (outside || active === edge) {
    event.preventDefault();
    entry.focus();
  }
}

/**
 * Возвращает фокус на элемент, которым оверлей открыли.
 *
 * Открывателя может не быть вовсе — оверлей открывается и из ngOnInit по query-параметру,
 * и из колбэка ответа сервера.
 */
export function restoreFocusTo(
  opener: HTMLElement | null,
  container?: HTMLElement,
): void {
  // Открыватель мог исчезнуть вместе с перезагрузкой списка под оверлеем — тогда фокус
  // не трогаем: браузер сам оставит его на body, и это лучше, чем упасть.
  if (!opener?.isConnected) {
    return;
  }
  // И не отбираем фокус, если он уже ушёл куда-то помимо оверлея: пользователь мог увести
  // его сам, а оверлей — закрыться колбэком ответа сервера.
  const active = document.activeElement;
  const focusIsOurs =
    !active || active === document.body || !!container?.contains(active);
  if (focusIsOurs) {
    opener.focus();
  }
}
